import React, { useCallback, useEffect, useState } from 'react'
import Loader from '../../../components/Live/Loader'
import { useSelector } from 'react-redux'
import { selectUserInformations } from '../../../features/authSlice'
import axios from 'axios'
import { API } from '../../../shared/api/Api'
import { showToast } from '../../../utils/utils'
import { motion, AnimatePresence } from 'framer-motion';


const DEBOUNCE_DELAY = 700;

// Animation variants
const tableContainerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3, ease: "easeInOut" }
    }
};

const rowVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: (i) => ({
        opacity: 1,
        x: 0,
        transition: { delay: i * 0.05, duration: 0.3 }
    }),
    exit: { opacity: 0, x: 50 }
};

const PermissionPage = () => {
    const [permissions, setPermissions] = useState([]);
    const [loader, setLoader] = useState(false);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const user = useSelector(selectUserInformations)

    const fetchPermissions = useCallback(async () => {
        setLoader(true)
        try {
            const { data } = await axios.get(API.Roles.app_roles_permissions_all, {
                params: { search: debouncedSearch },
                headers: {
                    Authorization: `Bearer ${user?.token}`
                }
            })
            setPermissions(data.data)
        } catch (error) {
            console.error('Error fetching permissions:', error);
            showToast('Error', 'Failed to fetch permissions', 'error');
        } finally {
            setLoader(false)
        }
    }, [debouncedSearch, user?.token])


    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions])

    // Search effect
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(handler);
    }, [search]);


    // Table Headers
    const tableHeaders = [
        "Permission UUID",
        "Permission Name",
        "Created At",
    ];

    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={tableContainerVariants}
            className="w-full mx-auto mt-8 px-2 sm:px-4 lg:px-0"
        >
            <div className="bg-white border border-gray-100 shadow-xl rounded-2xl p-4 sm:p-6 lg:p-8 relative">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                        <h1 className="font-anek-odia text-2xl sm:text-3xl font-semibold text-gray-900">
                            Permissions
                        </h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Browse and search all available system permissions.
                        </p>
                    </div>
                </div>

                {/* Header: Search */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="mt-5 flex flex-col sm:flex-row justify-between items-start sm:items-center border-t border-gray-100 pt-4 gap-3"
                >
                    <span className="text-sm sm:text-base font-medium text-gray-800">
                        All permissions
                    </span>
                    <motion.div whileHover={{ scale: 1.01 }} className="relative w-full sm:w-64">
                        <input
                            type="text"
                            placeholder="Search permissions..."
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full rounded-full border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary transition"
                        />
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-4.35-4.35M11 5a6 6 0 100 12 6 6 0 000-12z"
                                />
                            </svg>
                        </span>
                    </motion.div>
                </motion.div>

                {/* Table Container */}
                <div className="mt-6 rounded-2xl border border-gray-100 bg-white overflow-hidden">
                    <div className="w-full overflow-x-auto">
                        <div className="min-w-[720px]">
                            {loader && (
                                <motion.div
                                    initial={{ rotate: 0 }}
                                    animate={{ rotate: 360 }}
                                    transition={{ repeat: Infinity, duration: 1 }}
                                    className="flex justify-center items-center py-6"
                                >
                                    <Loader />
                                </motion.div>
                            )}

                            <table className="w-full table-auto">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        {tableHeaders.map(
                                            (header, idx) =>
                                                header && (
                                                    <motion.th
                                                        key={idx}
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ delay: idx * 0.1 }}
                                                        className="px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100"
                                                    >
                                                        {header}
                                                    </motion.th>
                                                )
                                        )}
                                    </tr>
                                </thead>

                                <tbody className="divide-y divide-gray-100">
                                    <AnimatePresence>
                                        {permissions.map((item, index) => (
                                            <motion.tr
                                                key={item.uuid}
                                                custom={index}
                                                initial="hidden"
                                                animate="visible"
                                                exit="exit"
                                                variants={rowVariants}
                                                className="hover:bg-gray-50 transition-colors cursor-auto text-sm"
                                            >
                                                <td className="px-4 sm:px-6 py-4 text-gray-900 font-medium">
                                                    {item.uuid}
                                                </td>
                                                <td className="px-4 sm:px-6 py-4 text-gray-700">
                                                    {item.name}
                                                </td>
                                                <td className="px-4 sm:px-6 py-4 text-gray-700">
                                                    {item.created_at}
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </AnimatePresence>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    )
}

export default PermissionPage
